import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { authMiddleware } from "~/lib/auth-middleware";
import { dbMiddleware } from "~/lib/db-middleware";
import {
  getNextFutureSummit,
  getUserSummitRegistration,
} from "~/lib/server/summit";
import { z } from "zod";
import { registerUserForSummitAndSendConfirmation } from "~/lib/server/summit-registration-workflow";
import type { SummitSelect } from "~/lib/schemas";
import { getDBWithInit } from "../db";
import { utmParametersSchema } from "~/lib/shared/utm";

const SummitRegistrationSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(100, "First name too long"),
  email: z.string().email("Valid email is required").max(320, "Email too long"),
  summitId: z.string().uuid("Valid summit ID is required"),
  redirectUrl: z
    .string()
    .url("Invalid redirect URL")
    .optional()
    .or(z.literal("")),
  utmParameters: utmParametersSchema,
});

const AuthenticatedRegistrationSchema = z.object({
  summitId: z.string().uuid("Valid summit ID is required"),
  redirectUrl: z
    .string()
    .url("Invalid redirect URL")
    .optional()
    .or(z.literal("")),
  utmParameters: utmParametersSchema,
});

export interface RegistrationResponse {
  summit: SummitSelect;
  redirectUrl: string | undefined;
}

function validateRedirectUrl(url: string | undefined): string | undefined {
  if (!url || url === "") return undefined;

  try {
    const parsedUrl = new URL(url);
    // Only allow https URLs in production for security
    if (
      parsedUrl.protocol !== "https:" &&
      process.env.NODE_ENV === "production"
    ) {
      throw new Error("Only HTTPS URLs are allowed in production");
    }
    return url;
  } catch {
    throw new Error("Invalid redirect URL format");
  }
}

export const getNextAvailableSummit = createServerFn({ method: "GET" })
  .middleware([dbMiddleware])
  .handler(async () => {
    try {
      const summit = await getNextFutureSummit();
      if (!summit) {
        return { error: "No upcoming summit found", summit: null };
      }
      return { error: null, summit };
    } catch (error) {
      console.error("Error fetching next available summit:", error);
      return {
        error:
          error instanceof Error ? error.message : "Failed to fetch summit",
        summit: null,
      };
    }
  });

export const getNextAvailableSummitOptions = queryOptions({
  queryKey: ["summit", "next-available"],
  queryFn: () => getNextAvailableSummit(),
  staleTime: 5 * 60 * 1000,
});

export const checkUserSummitRegistration = createServerFn({ method: "GET" })
  .middleware([dbMiddleware, authMiddleware])
  .handler(async ({ context }) => {
    try {
      const summit = await getNextFutureSummit();
      if (!summit) {
        return { isRegistered: false, summit: null, error: null };
      }

      const registration = await getUserSummitRegistration(
        context.user.id,
        summit.id,
      );

      return {
        isRegistered: registration.isRegistered,
        summit,
        error: null,
      };
    } catch (error) {
      console.error("Error checking user summit registration:", error);
      return {
        isRegistered: false,
        summit: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to check registration",
      };
    }
  });

export const checkUserSummitRegistrationOptions = queryOptions({
  queryKey: ["summit", "user-registration"],
  queryFn: () => checkUserSummitRegistration(),
  staleTime: 1 * 60 * 1000,
});

export const registerAuthenticatedUserForSummit = createServerFn({
  method: "POST",
})
  .middleware([dbMiddleware, authMiddleware])
  .validator(AuthenticatedRegistrationSchema)
  .handler(async ({ data, context }): Promise<RegistrationResponse> => {
    try {
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await (
        await getDBWithInit()
      ).query.summits.findFirst({
        where: (summits, { eq }) => eq(summits.id, data.summitId),
      });
      if (!summit) {
        throw new Error("No upcoming summit found");
      }

      await registerUserForSummitAndSendConfirmation({
        user: context.user,
        summit,
        utmParameters: data.utmParameters,
      });

      return {
        summit,
        redirectUrl: validatedRedirectUrl,
      };
    } catch (error) {
      console.error("Error in authenticated summit registration:", error);
      throw new Error(
        error instanceof Error
          ? error.message
          : "Failed to register for summit",
      );
    }
  });

export const registerUnauthenticatedUserForSummit = createServerFn({
  method: "POST",
})
  .middleware([dbMiddleware])
  .validator(SummitRegistrationSchema)
  .handler(async ({ data }): Promise<RegistrationResponse> => {
    try {
      const validatedRedirectUrl = validateRedirectUrl(data.redirectUrl);

      const summit = await (
        await getDBWithInit()
      ).query.summits.findFirst({
        where: (summits, { eq }) => eq(summits.id, data.summitId),
      });

      if (!summit) {
        throw new Error("No summit found");
      }

      let clerkUser = null;

      const { initializeClerkIfNeeded, getClerk } = await import(
        "~/lib/clerk-server"
      );
      await initializeClerkIfNeeded();
      const clerkClient = getClerk();
      const users = await clerkClient.users.getUserList({
        emailAddress: [data.email],
        limit: 1,
      });

      clerkUser = users.data[0];

      if (!clerkUser) {
        clerkUser = await clerkClient.users.createUser({
          emailAddress: [data.email],
          firstName: data.firstName,
        });
      }

      if (!clerkUser) {
        throw new Error("Failed to create or find user in Clerk");
      }

      const trimmedFirstName = data.firstName.trim();

      const { persistUserFromClerkProfile } = await import("~/lib/server/user");
      const user = await persistUserFromClerkProfile({
        clerkUserId: clerkUser.id,
        email: data.email,
        firstName: trimmedFirstName,
      });

      await registerUserForSummitAndSendConfirmation({
        user,
        summit,
        utmParameters: data.utmParameters,
      });

      // Store summit registration data in Clerk's publicMetadata for the sign-in attempt
      const redirectUrl =
        validatedRedirectUrl ||
        `${process.env.NODE_ENV === "production" ? "https://dreamcoachmatch.com" : "http://localhost:3000"}/dashboard`;

      return {
        summit,
        redirectUrl,
      };
    } catch (error) {
      console.error("Error preparing magic link registration:", error);
      throw new Error(
        `Failed to prepare registration: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  });
